/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */


import './RootSwitcher.scss';
import React, { useMemo } from 'react';
import Select, { GroupBase, StylesConfig } from 'react-select';
import { useHistory } from 'react-router-dom';
import { SettingStorage } from '@services/SettingStorage';
import { InitiativeTags } from '../../types/initiative';
import { AppRootMatcher, AppRouteMatcherInitiative } from '@routes/appRootMatcher';
import { AppCode } from '../../types/app';
import { naturalSort } from '../../utils';
import { getAppBadgeCodes, getAppTypeLabel } from '@utils/apps';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

interface BaseOption {
  value: string;
  label: string;
  firstInitiativeId: string;
}

interface Props {
  viewMode?: 'select'
  selectStyles?: StylesConfig<BaseOption, false>,
  routeMatcher?: AppRootMatcher,
  rootInitiatives?: AppRouteMatcherInitiative[],
}

const Badge = (props: { children: React.ReactNode }) => {
  const { children } = props;
  return (
    <span
      className={
        'd-none d-md-inline px-2 badge border border-ThemeTextMedium text-ThemeTextMedium user-select-none'
      }
    >
      {children}
    </span>
  );
};

const getAppBadges = (codes: string[]): React.JSX.Element[] => {
  return codes.map((code) => (
    <span key={code} className='ml-2'>
      <SimpleTooltip
        text={
          <>
            You are on the {code} plan.<br />
            See Admin / Account settings / Account management for details
          </>
        }
      >
        <Badge>{code}</Badge>
      </SimpleTooltip>
    </span>
  ));
};

export const OrganizationSwitcher = ({ viewMode, selectStyles, routeMatcher: routeHelper, rootInitiatives }: Props) => {
  const history = useHistory();

  const options = useMemo(() => {
    return routeHelper?.filterInitiatives((rootInitiatives || [])).map(r => ({
      value: r._id,
      label: r.name,
      firstInitiativeId: r.firstInitiativeId ?? r._id,
      isStaffOrganization: r.tags?.includes(InitiativeTags.StaffOrganization),
      isDemo: r.tags?.includes(InitiativeTags.DemoOrganization),
      appConfig: r.appConfig
    })).sort((a, b) => naturalSort(a.label, b.label)) ?? [];
  }, [rootInitiatives, routeHelper])

  if (!routeHelper || (options.length === 0)) {
    return null;
  }

  const combinedStyles: StylesConfig<BaseOption, false> = selectStyles ?? {
    container: (provided) => ({
      ...provided,
      display: 'inline-block',
      minWidth: '250px',
      margin: '0 .5rem'
    }),
    menu: (provider) => ({ ...provider, marginTop: '1px', }),
  }

  const selectedId = SettingStorage.getItem(routeHelper.storageKey);
  const selectedItem = options.find((o) => o.value === selectedId) ?? options[0];
  const onChange = (initiative: { value: string, firstInitiativeId: string }) => {
    SettingStorage.setItem(routeHelper.storageKey, initiative.value);
    history.push(routeHelper.generateUrl(initiative.firstInitiativeId));
  }

  const optionsByAppMap = new Map<AppCode | '', undefined | typeof options>(
    Object.values(AppCode).map(code => ([code, undefined]))
  );

  options.forEach(option => {
    const code = option.appConfig?.code ?? '';
    const optionsByApp = optionsByAppMap.get(code) ?? [];
    optionsByApp.push(option);
    optionsByAppMap.set(code, optionsByApp);
  });

  const groupedOptions: GroupBase<BaseOption>[] = [];
  Array.from(optionsByAppMap.keys()).forEach((code) => {
    groupedOptions.push({
      label: getAppTypeLabel(code) ?? '',
      options: optionsByAppMap.get(code) ?? [],
    });
  });

  const isSelectMode = viewMode === 'select';
  if (isSelectMode) {
    return (
      <div data-testid='root-switcher'>
        <Select
          styles={combinedStyles}
          onChange={(option) => {
            if (option) {
              onChange(option);
            }
          }}
          placeholder={'Search and select'}
          defaultMenuIsOpen={!isSelectMode}
          isSearchable={true}
          options={groupedOptions}
        />
      </div>
    );
  }

  const codes = getAppBadgeCodes(selectedItem)

  return (
    <>
      <div className='root-switcher-container dont_translate' data-testid='root-switcher-container'>
        <div className='d-none d-md-inline ml-4'>
          <SelectFactory
            selectType={SelectTypes.SingleSelect}
            isSearchable={true}
            isTransparent={true}
            classNames={{
              placeholder: (_provided) => 'text-truncate',
            }}
            onChange={(option) => {
              if (option) {
                // TODO: Fix type issue for options that have custom property like "firstInitiativeId"
                onChange(option as unknown as BaseOption);
              }
            }}
            value={selectedItem}
            options={groupedOptions}
          />
        </div>
      </div>
      {getAppBadges(codes)}
    </>
  );
};
